import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Download, ExternalLink, Presentation } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDateTime } from '@/lib/utils';
import { useGenerateDeck } from '../../hooks/useEstimates';
import { deckBlock } from '../../lib/actions';
import { errorBody, errorCode, errorMessage, MAY_STILL_FINISH } from '../../lib/errors';
import { deckPdfUrl } from '../../lib/format';
import type { Estimate } from '../../types';

interface Props {
  estimate: Estimate;
  /** The estimate is approved or presented (lib/actions.ts `generate-deck`). */
  canGenerate: boolean;
  onReload: () => void;
}

/** Generate deck / Open in Slides / Download PDF. Regeneration always makes a new file (plan 2). */
export function DeckPanel({ estimate, canGenerate, onReload }: Props) {
  const { t } = useTranslation('crm');
  const generate = useGenerateDeck(estimate.id);
  const block = deckBlock(estimate);
  const code = generate.error ? errorCode(generate.error) : null;
  const problems = generate.error ? errorBody(generate.error).problems ?? [] : [];

  const run = async () => {
    try {
      const { warnings } = await generate.mutateAsync();
      toast.success(t('estimates.deck.generated'), warnings.length ? { description: t('estimates.deck.generatedWithWarnings', { n: warnings.length }) } : undefined);
    } catch {
      // Shown below from generate.error.
    }
  };

  return (
    <Card role="region" aria-label={t('estimates.deck.title')}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('estimates.deck.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {estimate.deckUrl ? (
          <div className="flex flex-wrap gap-2">
            <Button asChild size="sm">
              <a href={estimate.deckUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-1" />{t('estimates.deck.open')}</a>
            </Button>
            {estimate.deckFileId && (
              <Button asChild size="sm" variant="outline">
                <a href={deckPdfUrl(estimate.deckFileId)} target="_blank" rel="noreferrer"><Download className="h-4 w-4 mr-1" />{t('estimates.deck.pdf')}</a>
              </Button>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground">{t('estimates.deck.none')}</p>
        )}
        {estimate.deckGeneratedAt && (
          <p className="text-xs text-muted-foreground">{t('estimates.deck.generatedAt', { when: formatDateTime(estimate.deckGeneratedAt) })}</p>
        )}
        {estimate.deckWarnings.length > 0 && (
          <ul className="list-disc pl-4 text-xs text-warning-dark">
            {estimate.deckWarnings.map((w, i) => <li key={`${w.code}-${i}`}>{w.message}</li>)}
          </ul>
        )}
        {canGenerate ? (
          <>
            <Button size="sm" variant={estimate.deckUrl ? 'outline' : 'default'} disabled={generate.isPending || block !== null} onClick={() => void run()}>
              <Presentation className="h-4 w-4 mr-1" />
              {generate.isPending ? t('estimates.deck.generating') : estimate.deckUrl ? t('estimates.deck.regenerate') : t('estimates.deck.generate')}
            </Button>
            {generate.isPending && <p className="text-xs text-muted-foreground">{t('estimates.deck.pendingHint')}</p>}
            {block === 'contingency' && <p className="text-xs text-muted-foreground">{t('estimates.deck.blockedContingency')}</p>}
            {block === 'in_progress' && !generate.isPending && (
              <p className="text-xs text-muted-foreground">
                {t('estimates.deck.inProgress')}{' '}
                <Button variant="link" size="sm" className="h-auto p-0" onClick={onReload}>{t('estimates.editor.reloadShort')}</Button>
              </p>
            )}
          </>
        ) : (
          (estimate.status === 'draft' || estimate.status === 'pending_approval') && (
            <p className="text-xs text-muted-foreground">{t('estimates.deck.afterApproval')}</p>
          )
        )}
        {generate.error ? (
          <Alert variant="destructive">
            <AlertDescription className="space-y-2">
              {code && MAY_STILL_FINISH.has(code) ? (
                <>
                  <p>{t('estimates.deck.mayStillFinish')}</p>
                  <Button size="sm" variant="outline" onClick={() => { generate.reset(); onReload(); }}>{t('estimates.editor.reloadShort')}</Button>
                </>
              ) : (
                <>
                  <p>{errorMessage(generate.error)}</p>
                  {problems.length > 0 && (
                    <ul className="list-disc pl-4 text-xs">{problems.map((p, i) => <li key={`${p.code}-${i}`}>{p.message}</li>)}</ul>
                  )}
                </>
              )}
            </AlertDescription>
          </Alert>
        ) : (
          estimate.deckStatus === 'failed' && estimate.deckError && (
            <p className="text-xs text-destructive">{t('estimates.deck.lastFailed', { error: estimate.deckError })}</p>
          )
        )}
      </CardContent>
    </Card>
  );
}
